import { useState } from 'react';
import { Ban } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useUserBlocks } from '@/hooks/useUserBlocks';
import { useAuth } from '@/hooks/useAuth';

interface BlockUserButtonProps {
  userId: string;
  username?: string;
  variant?: 'ghost' | 'outline' | 'default';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
  showLabel?: boolean;
}

export function BlockUserButton({
  userId,
  username,
  variant = 'ghost',
  size = 'sm',
  className,
  showLabel = true,
}: BlockUserButtonProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { user } = useAuth();
  const { isUserBlocked, blockUser, unblockUser, isBlocking, isUnblocking } = useUserBlocks();

  // Don't show block button for own profile
  if (!user || user.id === userId) return null;

  const isBlocked = isUserBlocked(userId);

  const handleBlock = () => {
    blockUser(userId);
    setIsDialogOpen(false);
  };

  const handleUnblock = () => {
    unblockUser(userId);
    setIsDialogOpen(false);
  };

  return (
    <>
      <Button
        variant={isBlocked ? 'default' : variant}
        size={size}
        onClick={(e) => {
          e.stopPropagation();
          setIsDialogOpen(true);
        }}
        className={className}
        disabled={isBlocking || isUnblocking}
        title={isBlocked ? 'Unblock this user' : 'Block this user'}
      >
        <Ban className="h-4 w-4" />
        {showLabel && <span className="ml-2">{isBlocked ? 'Unblock' : 'Block'}</span>}
      </Button>

      <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isBlocked ? 'Unblock' : 'Block'} {username || 'this user'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isBlocked 
                ? "You'll start seeing their reviews, ratings, and activities again."
                : "You won't see their reviews, ratings, or activities anymore. They won't be notified that you've blocked them."
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={isBlocked ? handleUnblock : handleBlock} 
              disabled={isBlocking || isUnblocking}
            >
              {isBlocking || isUnblocking 
                ? (isBlocked ? 'Unblocking...' : 'Blocking...') 
                : (isBlocked ? 'Unblock User' : 'Block User')
              }
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
